import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription, take } from 'rxjs';
import * as _ from 'lodash-es';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { environment } from '../../../../environments/environment';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-exec-dashboard',
    templateUrl: './dashboard-executive.component.html',
    styleUrls: ['./dashboard-executive.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardExecutiveComponent implements OnInit, OnDestroy {
  loading = false;
  dashboardConfig: any = null;
  componentRows: any[][] = [];
  sheetData: { [actionCode: string]: any[] } = {};
  errorMessage: string | null = null;

  private tabSub!: Subscription;

  constructor(
    private http: HttpClient,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.tabSub = this.workspaceService.setTabActive.subscribe((ws: any) => {
      if (!ws?.screen_key) return;
      if (!ws.filters?.loaded) return;

      const routeConfig = this.appService.ROUTES?.[ws.link];
      if (routeConfig?.type !== 'dashboard') return;

      this.loadDashboard(ws);
    });
  }

  private loadDashboard(activeScreen: any): void {
    this.loading = true;
    this.errorMessage = null;
    this.dashboardConfig = null;
    this.componentRows = [];
    this.sheetData = {};
    this.cdr.detectChanges();

    const routeConfig = this.appService.ROUTES?.[activeScreen.link];
    let apiGet = routeConfig?.api?.get;
    if (!apiGet) {
      this.errorMessage = 'No API configured for this dashboard.';
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    if (!apiGet.startsWith('/') && !apiGet.startsWith('http')) {
      apiGet = environment.base_url + '/' + apiGet;
    }

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};

    const params = _.merge(
      {},
      this.globalService.global?.globalParams || {},
      filterParams
    );

    this.postRequest(apiGet, params).pipe(take(1)).subscribe(
      (data: any) => {
        if (data?.dashboard) {
          this.dashboardConfig = data.dashboard;
          this.componentRows = data.dashboard.components || [];
          this.cdr.detectChanges();
          this.loadSheetData(activeScreen, params);
        } else {
          this.errorMessage = 'Dashboard configuration not found.';
          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      (err: any) => {
        console.error('[ExecDashboard] getDashboardByKey error:', err);
        this.errorMessage = 'Failed to load dashboard configuration.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadSheetData(activeScreen: any, params: any): void {
    const actionCodes = new Set<string>();
    for (const row of this.componentRows) {
      for (const comp of row) {
        if (comp.action_code) {
          actionCodes.add(comp.action_code);
        }
      }
    }

    if (actionCodes.size === 0) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    let pending = actionCodes.size;
    actionCodes.forEach((code) => {
      let url = 'loadJsonObject?action_code=' + code;
      if (!url.startsWith('/') && !url.startsWith('http')) {
        url = environment.base_url + '/' + url;
      }

      this.postRequest(url, params).pipe(take(1)).subscribe(
        (data: any) => {
          if (data?.jsonObject) {
            this.sheetData[code] = data.jsonObject;
          } else if (Array.isArray(data)) {
            this.sheetData[code] = data;
          } else if (data?.data) {
            this.sheetData[code] = data.data;
          }
          pending--;
          if (pending <= 0) {
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        (err: any) => {
          console.error('[ExecDashboard] loadJsonObject error for', code, err);
          pending--;
          if (pending <= 0) {
            this.loading = false;
            this.cdr.detectChanges();
          }
        }
      );
    });
  }

  private postRequest(url: string, params: any) {
    const parts: string[] = [];
    _.forEach(params, (v: any, k: string) => {
      if (v !== null && v !== undefined) {
        parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      }
    });
    const body = parts.join('&');
    return this.http.post(url, body, {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
      }),
    });
  }

  getComponentData(comp: any): any[] {
    return this.sheetData[comp.action_code] || [];
  }

  getColumns(comp: any): any[] {
    try {
      const extra = typeof comp.extraOptions === 'string'
        ? safeJsonInput<any>(comp.extraOptions, null, 'dashboard-executive.parse1')
        : comp.extraOptions;
      const cols = extra?.componentSettings?.tableOptions?.columns
        || extra?.tableOptions?.columns
        || null;
      if (cols && cols.length > 0) return cols;
    } catch { /* ignore */ }

    const data = this.getComponentData(comp);
    if (data.length > 0) {
      return Object.keys(data[0])
        .filter(k => !k.startsWith('_') && !k.startsWith('$$'))
        .map(k => ({ title: k, data: k }));
    }
    return [];
  }

  getCellValue(row: any, col: any): string {
    const key = col.data || col.field || col.name;
    if (!key) return '';
    const val = row[key];
    if (val === null || val === undefined) return '';
    if (col.formatAmount || col.format === 'amount') {
      return this.formatNumber(val);
    }
    return String(val);
  }

  private formatNumber(val: any): string {
    const num = Number(val);
    if (isNaN(num)) return String(val);
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  private readonly CHART_TYPES = [
    'horizontalBusinessTreeMap', 'treemap', 'chart', 'barChart',
    'pieChart', 'lineChart', 'donut', 'gauge',
  ];

  isDatatable(comp: any): boolean {
    return !this.CHART_TYPES.includes(comp.component_type);
  }

  isChart(comp: any): boolean {
    return this.CHART_TYPES.includes(comp.component_type);
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    if (this.tabSub) this.tabSub.unsubscribe();
  }
}
